import { useCallback, useRef, type RefCallback } from "react";
import {
  get,
  type FieldErrors,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";

const defaultFocusableSelector = [
  '[role="combobox"]:not([aria-disabled="true"])',
  '[tabindex="0"]',
  'input:not([disabled]):not([type="hidden"])',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'select:not([disabled])',
].join(", ");

const defaultScrollOptions: ScrollIntoViewOptions = {
  behavior: "smooth",
  block: "center",
};

const isAvailableFocusTarget = (element: HTMLElement) => {
  if (
    !element.isConnected ||
    element.matches(':disabled, input[type="hidden"]') ||
    element.closest(
      '[hidden], [inert], [aria-hidden="true"], [aria-disabled="true"]',
    )
  ) {
    return false;
  }

  const view = element.ownerDocument.defaultView;
  if (!view) {
    return false;
  }

  const { visibility } = view.getComputedStyle(element);
  if (visibility === "hidden" || visibility === "collapse") {
    return false;
  }

  for (
    let ancestor: HTMLElement | null = element;
    ancestor;
    ancestor = ancestor.parentElement
  ) {
    if (view.getComputedStyle(ancestor).display === "none") {
      return false;
    }
  }

  return true;
};

interface UseFocusFirstInvalidFieldOptions<
  TFieldValues extends FieldValues,
> {
  fieldOrder: readonly FieldPath<TFieldValues>[];
  focusableSelector?: string;
  scrollOptions?: ScrollIntoViewOptions;
}

export default function useFocusFirstInvalidField<
  TFieldValues extends FieldValues,
  TExternalTarget extends string = never,
>({
  fieldOrder,
  focusableSelector = defaultFocusableSelector,
  scrollOptions = defaultScrollOptions,
}: UseFocusFirstInvalidFieldOptions<TFieldValues>) {
  type TargetName = FieldPath<TFieldValues> | TExternalTarget;

  const targetElements = useRef(
    new Map<TargetName, HTMLElement>(),
  );
  const targetRefCallbacks = useRef(
    new Map<TargetName, RefCallback<HTMLElement>>(),
  );

  const registerValidationTarget = useCallback((targetName: TargetName) => {
    const existingCallback = targetRefCallbacks.current.get(targetName);
    if (existingCallback) {
      return existingCallback;
    }

    const targetRef: RefCallback<HTMLElement> = (element) => {
      if (element) {
        targetElements.current.set(targetName, element);
        return;
      }

      targetElements.current.delete(targetName);
    };
    targetRefCallbacks.current.set(targetName, targetRef);
    return targetRef;
  }, []);

  const focusValidationTarget = useCallback(
    (targetName: TargetName) => {
      const fieldContainer = targetElements.current.get(targetName);
      if (!fieldContainer) {
        return false;
      }

      const focusTargets = [
        ...(fieldContainer.matches(focusableSelector) ? [fieldContainer] : []),
        ...fieldContainer.querySelectorAll<HTMLElement>(focusableSelector),
      ];

      for (const focusTarget of focusTargets) {
        if (!isAvailableFocusTarget(focusTarget)) {
          continue;
        }

        focusTarget.focus({ preventScroll: true });
        if (focusTarget.ownerDocument.activeElement === focusTarget) {
          fieldContainer.scrollIntoView?.(scrollOptions);
          return true;
        }
      }

      return false;
    },
    [focusableSelector, scrollOptions],
  );

  const focusFirstInvalidField = useCallback(
    (
      fieldErrors: FieldErrors<TFieldValues>,
      externalInvalidTargets: readonly TExternalTarget[] = [],
    ) => {
      const invalidFormTargets = fieldOrder.filter((fieldName) =>
        Boolean(get(fieldErrors, fieldName)),
      );
      const invalidTargets: TargetName[] = [
        ...invalidFormTargets,
        ...externalInvalidTargets,
      ];

      for (const targetName of invalidTargets) {
        if (focusValidationTarget(targetName)) {
          return targetName;
        }
      }

      return undefined;
    },
    [fieldOrder, focusValidationTarget],
  );

  return {
    focusFirstInvalidField,
    registerValidationTarget,
  };
}
